'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiRequirement, type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertAwardToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { type QueryKey } from '@tanstack/react-query';
import { type RouterOutputs } from '../../utils/trpc';
import Input from '../UI/Input';
import { type BorderState, FactiiiSearchResult } from './FactiiiSearchResult';
import { FactiiiTagInputs } from './FactiiiTagInputs';
type RequirementResult = {
  tag: FactiiiTag;
  results: {
    completed: boolean;
    description: string;
    requirement: FactiiiRequirement;
  }[];
};

// An award staged for giving alongside tagging. Awards "pretend to be
// factiiis" — they render in the Newly added list like a factiii does, with a
// per-award Public/Private toggle and a "how many" count.
export type SelectedAward = {
  id: number;
  title: string;
  image: string | null | undefined;
  cost: number;
  description?: string | null;
  anonymous: boolean;
  count: number;
};
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: (counts: FactiiiTag[]) => void;
  requirementsMet: RequirementResult[];
  myFactiiiTagsData: RouterOutputs['factiiis']['myFactiiiTags'] | undefined;
  setCameraOpenId: (id: number | null) => void;
  addSpaceTime: (tag: FactiiiTag, entry: {
    location?: string;
    date?: string;
    time?: string;
    timezone?: {
      value: string;
      label: string;
      offset: number;
    };
  }) => boolean;
  removeSpaceTime: (tag: FactiiiTag, index: number) => void;
  defaultTimezone: {
    value: string;
    label: string;
    offset: number;
  };
  queryKey: QueryKey;
  isPrivatePost?: boolean;
  selectedAwards?: SelectedAward[];
  onRemoveAward?: (id: number) => void;
  onUpdateAward?: (id: number, patch: Partial<SelectedAward>) => void;
};
const computeBorderState = (tag: FactiiiTag, requirementsMet: RequirementResult[]): BorderState => {
  if (tag.isRemovedFactiii) return 'removed';
  const tagReqs = requirementsMet.find(r => r.tag.factiiiId === tag.factiiiId);
  const allMet = tagReqs?.results.every(r => r.completed) ?? false;
  return allMet ? 'ready' : 'incomplete';
};

// Persistent selected list, above the pills. This is the edit surface — each
// row uses the same FactiiiSearchResult card as Search, collapsed by default
// with its description + inputs behind a caret to keep height minimal.
export const SelectedFactiiis = props => {
  const $ = _c(32);
  let t0;
  if ($[0] !== props.selectedAwards) {
    t0 = props.selectedAwards ?? [];
    $[0] = props.selectedAwards;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selectedAwards = t0;
  if (props.myPostFactiiiCounts.length === 0 && selectedAwards.length === 0) {
    return null;
  }
  let t1;
  if ($[2] !== props) {
    t1 = tag => {
      const isFromServer = props.myFactiiiTagsData?.some(f => f.factiiiId === tag.factiiiId);
      if (isFromServer) {
        props.setMyPostFactiiiCounts(props.myPostFactiiiCounts.map(f_0 => f_0.factiiiId === tag.factiiiId ? {
          ...f_0,
          isRemovedFactiii: !f_0.isRemovedFactiii
        } : f_0));
      } else {
        props.setMyPostFactiiiCounts(props.myPostFactiiiCounts.filter(f_1 => f_1.factiiiId !== tag.factiiiId));
      }
    };
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleTrash = t1;
  let t2;
  if ($[4] !== props.requirementsMet) {
    t2 = tag_0 => {
      if (tag_0.isRemovedFactiii) {
        return [];
      }
      return props.requirementsMet.find(r => r.tag.factiiiId === tag_0.factiiiId)?.results.filter(_temp).map(_temp2) ?? [];
    };
    $[4] = props.requirementsMet;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const missingFor = t2;
  let t3;
  if ($[6] !== handleTrash || $[7] !== missingFor || $[8] !== props.addSpaceTime || $[9] !== props.defaultTimezone || $[10] !== props.isPrivatePost || $[11] !== props.myFactiiiTagsData || $[12] !== props.myPostFactiiiCounts || $[13] !== props.queryKey || $[14] !== props.removeSpaceTime || $[15] !== props.requirementsMet || $[16] !== props.setCameraOpenId || $[17] !== props.setMyPostFactiiiCounts) {
    t3 = tag_1 => <FactiiiSearchResult key={tag_1.factiiiId} tag={tag_1} replyConfig={tag_1.replyConfig} collapsible={true} borderState={computeBorderState(tag_1, props.requirementsMet)} missing={missingFor(tag_1)} handleClick={_temp3} trashHandler={() => handleTrash(tag_1)}>{props.myFactiiiTagsData && <FactiiiTagInputs tag={tag_1} requirementsMet={props.requirementsMet} setCameraOpenId={props.setCameraOpenId} defaultTimezone={props.defaultTimezone} myPostFactiiiCounts={props.myPostFactiiiCounts} setMyPostFactiiiCounts={props.setMyPostFactiiiCounts} addSpaceTime={props.addSpaceTime} removeSpaceTime={props.removeSpaceTime} queryKey={props.queryKey} isPrivatePost={props.isPrivatePost} />}</FactiiiSearchResult>;
    $[6] = handleTrash;
    $[7] = missingFor;
    $[8] = props.addSpaceTime;
    $[9] = props.defaultTimezone;
    $[10] = props.isPrivatePost;
    $[11] = props.myFactiiiTagsData;
    $[12] = props.myPostFactiiiCounts;
    $[13] = props.queryKey;
    $[14] = props.removeSpaceTime;
    $[15] = props.requirementsMet;
    $[16] = props.setCameraOpenId;
    $[17] = props.setMyPostFactiiiCounts;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const renderTag = t3;
  let t4;
  if ($[19] !== props) {
    t4 = award => <FactiiiSearchResult key={`award-${award.id}`} tag={convertAwardToFactiiiTag(award)} collapsible={true} borderState="ready" handleClick={_temp4} trashHandler={() => props.onRemoveAward?.(award.id)}><div className="space-y-3"><Input type="number" label="How many" value={String(award.count)} onChange={v => props.onUpdateAward?.(award.id, {
          count: Math.max(1, Number(v) || 1)
        })} /><div className="flex items-center justify-between"><span className="text-xs text-textSecondary">{award.count} × {award.cost.toLocaleString()} ={" "}{(award.count * award.cost).toLocaleString()} trons</span><button type="button" onClick={() => props.onUpdateAward?.(award.id, {
            anonymous: !award.anonymous
          })} className={`rounded-full border px-3 py-1 text-xs font-emphasis transition-all ${award.anonymous ? "border-primary bg-primary/10 text-primary" : "border-ds-border-standard text-textSecondary hover:border-textSecondary"}`}>{award.anonymous ? "Private" : "Public"}</button></div></div></FactiiiSearchResult>;
    $[19] = props;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const renderAward = t4;
  let t5;
  let t6;
  let t7;
  if ($[21] !== props.myPostFactiiiCounts || $[22] !== renderAward || $[23] !== renderTag || $[24] !== selectedAwards) {
    const newlyAdded = props.myPostFactiiiCounts.filter(_temp5);
    const alreadyAdded = props.myPostFactiiiCounts.filter(_temp6);
    const newlyAddedCount = newlyAdded.length + selectedAwards.length;
    t5 = "mb-2 space-y-2";
    t6 = newlyAddedCount > 0 && <div className="space-y-1.5"><p className="text-xs font-emphasis text-textSecondary">Newly added ({newlyAddedCount})</p>{newlyAdded.map(renderTag)}{selectedAwards.map(renderAward)}</div>;
    t7 = alreadyAdded.length > 0 && <div className="space-y-1.5"><p className="text-xs font-emphasis text-textSecondary">Already added ({alreadyAdded.length})</p>{alreadyAdded.map(renderTag)}</div>;
    $[21] = props.myPostFactiiiCounts;
    $[22] = renderAward;
    $[23] = renderTag;
    $[24] = selectedAwards;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};
function _temp(r_0) {
  return !r_0.completed;
}
function _temp2(r_1) {
  return r_1.description;
}
function _temp3() {}
function _temp4() {}
function _temp5(t) {
  return t.isNewFactiii;
}
function _temp6(t_0) {
  return !t_0.isNewFactiii;
}